import { useEffect, useState } from 'react';
import { formatMoney } from '@/lib/format';
import { MIN_DISCOUNT_UNIT_PRICE } from '@/lib/salesSettings';
import ProductImage from '@/components/feature/ProductImage';
import type { CartItem, LinePricing } from '@/lib/types';

interface CartPanelProps {
  cart: CartItem[];
  pricing: Record<string, LinePricing>;
  currencySymbol: string;
  canApplyDiscount: boolean;
  maxPercentage: number;
  isAdmin: boolean;
  fixedAllowed: boolean;
  taxesEnabled: boolean;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  onIncrement: (id: string) => void;
  onDecrement: (id: string) => void;
  onRemove: (id: string) => void;
  onEditDiscount: (id: string) => void;
  onApplyPercent: (id: string, percent: number | null) => void;
  onCheckout: () => void;
  selectedLineId?: string | null;
  onSelectLine?: (id: string) => void;
}

export default function CartPanel({
  cart,
  pricing,
  currencySymbol,
  canApplyDiscount,
  maxPercentage,
  isAdmin,
  fixedAllowed,
  taxesEnabled,
  subtotal,
  discount,
  tax,
  total,
  onIncrement,
  onDecrement,
  onRemove,
  onEditDiscount,
  onApplyPercent,
  onCheckout,
  selectedLineId,
  onSelectLine,
}: CartPanelProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto -mx-2 px-2">
        {cart.length === 0 ? (
          <div className="py-16 text-center">
            <i className="ri-shopping-basket-line text-4xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-600">
              El carrito está vacío.
              <br />
              Selecciona productos para comenzar la venta.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-background-200">
            {cart.map((item) => {
              const p = pricing[item.product.id] ?? {
                unitPrice: item.product.sale_price,
                discountAmount: item.discount,
                discountPct: null,
                discountType: null,
                isWholesale: false,
                autoDiscount: 0,
                autoDiscountPct: null,
                autoDiscountType: null,
                manualDiscount: 0,
                manualDiscountPct: null,
                manualDiscountType: null,
                manualDiscountValue: 0,
                manualAuthorizedByName: null,
              };
              const lineTotal = round2(p.unitPrice * item.quantity - p.discountAmount);
              const isSelected = selectedLineId === item.product.id;
              const hasManual = p.manualDiscount > 0 && p.manualDiscountType != null;
              const hasProductDiscount = p.autoDiscountType === 'product';
              const hasOffer = p.autoDiscountType === 'offer';
              return (
                <li
                  key={item.product.id}
                  onClick={() => onSelectLine?.(item.product.id)}
                  className={`py-3 flex items-start gap-3 cursor-pointer ${
                    isSelected ? 'bg-primary-50/60 rounded-md -mx-1 px-1' : ''
                  }`}
                >
                  <ProductImage
                    url={item.product.image_url}
                    name={item.product.name}
                    className="w-11 h-11 rounded-md object-cover bg-background-100 shrink-0"
                    placeholderClassName="w-11 h-11 rounded-md shrink-0 p-0"
                    iconClassName="text-base"
                    showName={false}
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-foreground-950 leading-snug">
                        {item.product.name}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(item.product.id);
                        }}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-700 hover:bg-accent-50 shrink-0 cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-6-line text-sm" />
                      </button>
                    </div>

                    {(p.isWholesale || hasProductDiscount || hasOffer || hasManual) && (
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        {p.isWholesale && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-500 text-background-50">
                            MAYORISTA
                          </span>
                        )}
                        {hasManual && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary-500 text-background-50">
                            <i className="ri-price-tag-3-fill" />
                            DESCUENTO EXTRA
                            {p.manualDiscountType === 'percent' ? ` ${p.manualDiscountPct}%` : ''}
                          </span>
                        )}
                        {hasProductDiscount && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-secondary-500 text-background-50">
                            DESC. PRODUCTO -{p.autoDiscountPct}%
                          </span>
                        )}
                        {hasOffer && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-secondary-500 text-background-50">
                            DESC. OFERTA -{p.autoDiscountPct}%
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDecrement(item.product.id);
                          }}
                          className="w-7 h-7 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-subtract-line text-sm" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-foreground-950">
                          {item.quantity}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onIncrement(item.product.id);
                          }}
                          className="w-7 h-7 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-add-line text-sm" />
                        </button>
                      </div>
                      <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                        {formatMoney(lineTotal, currencySymbol)}
                      </p>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2">
                      <QuickDiscount
                        itemId={item.product.id}
                        percent={p.manualDiscountType === 'percent' ? p.manualDiscountPct : null}
                        maxPercentage={maxPercentage}
                        isAdmin={isAdmin}
                        onApply={onApplyPercent}
                        onRequestAuth={onEditDiscount}
                      />
                      {fixedAllowed && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditDiscount(item.product.id);
                          }}
                          className="w-7 h-7 rounded-md border border-background-300 flex items-center justify-center text-foreground-500 hover:bg-background-100 shrink-0 cursor-pointer"
                          title="Más opciones (monto fijo / autorización)"
                        >
                          <i className="ri-equalizer-line text-xs" />
                        </button>
                      )}
                    </div>

                    <div className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                      <span className={p.isWholesale ? 'text-accent-700 font-medium' : ''}>
                        {formatMoney(p.unitPrice, currencySymbol)} c/u
                      </span>
                    </div>

                    {(hasManual || p.autoDiscount > 0) && (
                      <div className="mt-1 space-y-0.5 text-xs">
                        {p.autoDiscount > 0 && (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-secondary-900">
                              {hasOffer ? 'Descuento oferta' : 'Descuento producto'}
                              {p.autoDiscountPct != null ? ` ${p.autoDiscountPct}%` : ''}
                            </span>
                            <span className="font-medium text-secondary-900">
                              -{formatMoney(p.autoDiscount, currencySymbol)}
                            </span>
                          </div>
                        )}
                        {hasManual && (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-accent-700">
                              Descuento extra
                              {p.manualDiscountType === 'percent' ? ` ${p.manualDiscountPct}%` : ' (monto)'}
                            </span>
                            <span className="font-medium text-accent-700">
                              -{formatMoney(p.manualDiscount, currencySymbol)}
                            </span>
                          </div>
                        )}
                        {hasManual && p.manualAuthorizedByName && (
                          <p className="text-[10px] text-foreground-400">
                            Autorizado por {p.manualAuthorizedByName}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Totales */}
      <div className="border-t border-background-200 pt-3 space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-foreground-600">Subtotal</span>
          <span className="font-medium text-foreground-900">{formatMoney(subtotal, currencySymbol)}</span>
        </div>
        {discount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-foreground-600">Descuento</span>
            <span className="font-medium text-accent-700">-{formatMoney(discount, currencySymbol)}</span>
          </div>
        )}
        {taxesEnabled && (
          <div className="flex items-center justify-between">
            <span className="text-foreground-600">Impuesto</span>
            <span className="font-medium text-foreground-900">{formatMoney(tax, currencySymbol)}</span>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-background-200">
          <span className="font-heading font-semibold text-foreground-950">TOTAL</span>
          <span className="font-heading text-xl font-bold text-foreground-950">
            {formatMoney(total, currencySymbol)}
          </span>
        </div>
        <button
          onClick={onCheckout}
          disabled={cart.length === 0}
          className="mt-3 w-full rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-3 text-base font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          COBRAR
        </button>
      </div>
    </div>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

interface QuickDiscountProps {
  itemId: string;
  percent: number | null;
  maxPercentage: number;
  isAdmin: boolean;
  onApply: (id: string, percent: number | null) => void;
  onRequestAuth: (id: string) => void;
}

// Campo de descuento manual por porcentaje, aplicado en vivo sobre la línea.
function QuickDiscount({
  itemId,
  percent,
  maxPercentage,
  isAdmin,
  onApply,
  onRequestAuth,
}: QuickDiscountProps) {
  const [text, setText] = useState(percent != null ? String(percent) : '');

  useEffect(() => {
    setText(percent != null ? String(percent) : '');
  }, [percent]);

  const num = parseFloat(text) || 0;
  const needsAuth = !isAdmin && maxPercentage > 0 && num > maxPercentage;

  const handleChange = (raw: string) => {
    setText(raw);
    const n = parseFloat(raw) || 0;
    if (n <= 0) {
      onApply(itemId, null);
      return;
    }
    if (n > 100) return;
    // Si supera el máximo permitido, espera autorización de un administrador.
    if (!isAdmin && maxPercentage > 0 && n > maxPercentage) return;
    onApply(itemId, n);
  };

  return (
    <>
      <div
        className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${
          percent != null
            ? 'border-primary-300 bg-primary-50'
            : 'border-background-300 bg-background-50'
        }`}
      >
        <i
          className={`ri-percent-line text-xs ${
            percent != null ? 'text-primary-700' : 'text-foreground-500'
          }`}
        />
        <input
          type="number"
          min="0"
          max="100"
          inputMode="decimal"
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder="0"
          title="Descuento manual (%)"
          className="w-12 bg-transparent text-xs font-semibold text-foreground-900 focus:outline-none placeholder:font-normal placeholder:text-foreground-400"
        />
        <span className="text-[11px] text-foreground-500 whitespace-nowrap">% dto.</span>
      </div>
      {needsAuth && (
        <span className="inline-flex items-center gap-1 text-[11px] text-accent-700">
          <i className="ri-shield-keyhole-line" />
          Requiere autorización
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRequestAuth(itemId);
            }}
            className="font-semibold underline cursor-pointer"
          >
            Autorizar
          </button>
        </span>
      )}
    </>
  );
}